import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { fontsApi, type FontsListParams } from '../api/endpoints';
import type { Font, FontUpdateRequest, FontUploadResult, Id, OkResponse } from '../types/api';
import { queryKeys } from './keys';

export function useFonts(params: FontsListParams = {}) {
  return useQuery({
    queryKey: queryKeys.fonts(params),
    queryFn: () => fontsApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useFontTeams() {
  return useQuery({
    queryKey: queryKeys.fontTeams,
    queryFn: () => fontsApi.teams(),
    staleTime: 5 * 60_000,
  });
}

export function useUploadFonts() {
  const qc = useQueryClient();
  return useMutation<FontUploadResult, ApiError, { files: File[]; team: string }>({
    mutationFn: ({ files, team }) => fontsApi.upload(files, team),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.fontsRoot }),
  });
}

export function useUpdateFont() {
  const qc = useQueryClient();
  return useMutation<Font, ApiError, { id: Id; payload: FontUpdateRequest }>({
    mutationFn: ({ id, payload }) => fontsApi.update(id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.fontsRoot }),
  });
}

export function useDeleteFont() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiError, Id>({
    mutationFn: (id) => fontsApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.fontsRoot }),
  });
}
